import { Edge, Node } from "@xyflow/react";
import { NodeType } from "components/CC/types/enums";

/**
 * Calculates sort order for nodes based on their position in the flow.
 * Each separate path (starting from a start node) has its own numbering starting from 1.
 * Uses BFS traversal for each path independently.
 * Returns a map of nodeId -> sortOrder (1-indexed per path)
 */
export const calculateSortOrder = (nodes: Node[], edges: Edge[]): Map<string, number> => {
    const sortOrderMap = new Map<string, number>();

    if (nodes.length === 0) return sortOrderMap;

    // Build adjacency list (source -> targets)
    const adjacencyList = new Map<string, string[]>();
    edges.forEach((edge) => {
        const targets = adjacencyList.get(edge.source) || [];
        targets.push(edge.target);
        adjacencyList.set(edge.source, targets);
    });

    // Find start nodes (nodes with type 'startNode')
    const startNodes = nodes.filter((node) => node.type === NodeType.Start);

    // Sort start nodes by y position (top to bottom) for consistent ordering
    const sortedStartNodes = startNodes.sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));

    // Track globally visited nodes to avoid processing shared nodes multiple times
    const globalVisited = new Set<string>();

    // Process each start node's path independently
    sortedStartNodes.forEach((startNode) => {
        if (globalVisited.has(startNode.id)) return;

        // BFS traversal for this path
        let currentOrder = 1;
        const queue: string[] = [startNode.id];
        const pathVisited = new Set<string>([startNode.id]);

        while (queue.length > 0) {
            const nodeId = queue.shift() as string;

            // Skip if already assigned an order (shared node from another path)
            if (!sortOrderMap.has(nodeId)) {
                sortOrderMap.set(nodeId, currentOrder);
                currentOrder++;
            }
            globalVisited.add(nodeId);

            // Get connected nodes and sort by y position (top to bottom for branches)
            const connectedIds = adjacencyList.get(nodeId) || [];
            const connectedNodes = connectedIds
                .map((id) => nodes.find((n) => n.id === id))
                .filter((n): n is Node => n !== undefined)
                .sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));

            connectedNodes.forEach((node) => {
                if (!pathVisited.has(node.id)) {
                    queue.push(node.id);
                    pathVisited.add(node.id);
                }
            });
        }
    });

    // Handle any disconnected nodes (nodes not reachable from any start node)
    let disconnectedOrder = 1;
    nodes.forEach((node) => {
        if (!sortOrderMap.has(node.id)) {
            sortOrderMap.set(node.id, disconnectedOrder);
            disconnectedOrder++;
        }
    });

    return sortOrderMap;
};
